/**
 * Real-Time Dashboard Hook
 * ========================
 * Custom React hook for WebSocket-based real-time dashboard updates.
 * Handles metric updates, AI insights, sync status, and alerts.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '@/utils/safeToast';
import { devLog } from '@/lib/devLog';

const WS_URL = (typeof window !== 'undefined' ? window.location.origin : '')?.replace('https://', 'wss://').replace('http://', 'ws://');

export const useRealTimeDashboard = ({
  userId,
  businessId,
  enabled = true,
  onMetricUpdate,
  onAIInsight,
  onSyncStatus,
  onAlert
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [realtimeMetrics, setRealtimeMetrics] = useState({});
  const [realtimeInsights, setRealtimeInsights] = useState([]);
  const [syncStatuses, setSyncStatuses] = useState({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!enabled || !userId || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const wsUrl = `${WS_URL}/api/ws/dashboard/${userId}${businessId ? `?business_id=${businessId}` : ''}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        devLog('🔌 Real-time dashboard connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleMessage(message);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onclose = (event) => {
        devLog('🔌 WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Attempt reconnection with exponential backoff
        if (enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current++;
          devLog(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
    }
    // `handleMessage` is intentionally excluded — it's declared below
    // and references state setters which never change identity.
    // Including it would force a reconnect on every render.
    // eslint-disable-next-line
  }, [enabled, userId, businessId]);

  // Handle incoming messages
  const handleMessage = useCallback((message) => {
    setLastUpdate(new Date(message.timestamp));

    switch (message.type) {
      case 'connected':
        devLog('✅ Dashboard WebSocket confirmed');
        break;

      case 'heartbeat':
      case 'pong':
        // Keep-alive messages, no action needed
        break;

      case 'metric_update':
        setRealtimeMetrics(prev => ({
          ...prev,
          [message.metric]: {
            value: message.value,
            change: message.change,
            timestamp: message.timestamp
          }
        }));
        onMetricUpdate?.(message);
        break;

      case 'ai_insight':
        setRealtimeInsights(prev => [message.insight, ...prev.slice(0, 9)]);
        onAIInsight?.(message.insight);
        // Show toast for high-priority insights
        if (message.insight?.priority === 'high' || message.insight?.priority === 'critical') {
          toast.info(message.insight.title || 'New AI Insight', {
            description: message.insight.description?.slice(0, 100),
            action: {
              label: 'View',
              onClick: () => onAIInsight?.(message.insight)
            }
          });
        }
        break;

      case 'sync_status':
        setSyncStatuses(prev => ({
          ...prev,
          [message.provider]: {
            status: message.status,
            recordsSynced: message.records_synced,
            timestamp: message.timestamp
          }
        }));
        onSyncStatus?.(message);
        // Show toast for sync completion
        if (message.status === 'completed') {
          toast.success(`${message.provider} sync complete`, {
            description: `${message.records_synced} records synced`
          });
        } else if (message.status === 'failed') {
          toast.error(`${message.provider} sync failed`, {
            description: 'Check integration settings'
          });
        }
        break;

      case 'alert':
        onAlert?.(message.alert);
        // Show toast for alerts
        toast.warning(message.alert?.title || 'Alert', {
          description: message.alert?.message,
          duration: 10000
        });
        break;

      default:
        devLog('Unknown message type:', message.type);
    }
  }, [onMetricUpdate, onAIInsight, onSyncStatus, onAlert]);

  // Send ping to keep connection alive
  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  // Request manual update
  const requestUpdate = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'request_update' }));
    }
  }, []);

  // Subscribe to specific metrics
  const subscribeToMetrics = useCallback((metrics) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', metrics }));
    }
  }, []);

  // Disconnect
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    
    // Set up ping interval
    const pingInterval = setInterval(sendPing, 25000);
    
    return () => {
      clearInterval(pingInterval);
      disconnect();
    };
  }, [connect, disconnect, sendPing]);

  // Reconnect when userId or businessId changes
  useEffect(() => {
    if (enabled) {
      disconnect();
      connect();
    }
    // `connect`/`disconnect` are intentionally excluded — including them
    // would cause an infinite reconnect loop since they depend on the
    // same `userId`/`businessId`/`enabled` triple this effect reacts to.
    // eslint-disable-next-line
  }, [userId, businessId, enabled]);

  return {
    isConnected,
    lastUpdate,
    realtimeMetrics,
    realtimeInsights,
    syncStatuses,
    requestUpdate,
    subscribeToMetrics,
    disconnect
  };
};

/**
 * Real-Time Metric Display Component
 * Shows a metric with real-time update animation
 */
export const RealTimeMetric = ({ 
  metricKey, 
  label, 
  format = 'number', 
  realtimeData,
  fallbackValue 
}) => {
  const data = realtimeData?.[metricKey];
  const value = data?.value ?? fallbackValue;
  const change = data?.change;
  const isUpdated = data?.timestamp && (Date.now() - new Date(data.timestamp).getTime() < 5000);

  const formatValue = (val) => {
    if (format === 'currency') return `$${val?.toLocaleString() || 0}`;
    if (format === 'percent') return `${val?.toFixed(1) || 0}%`;
    return val?.toLocaleString() || '0';
  };

  return (
    <div className={`transition-all duration-300 ${isUpdated ? 'scale-105 bg-emerald-50' : ''}`}>
      <span className="text-xs text-slate-500">{label}</span>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-bold ${isUpdated ? 'text-emerald-600' : 'text-slate-900'}`}>
          {formatValue(value)}
        </span>
        {change !== undefined && change !== 0 && (
          <span className={`text-sm font-medium ${change > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {change > 0 ? '+' : ''}{change.toFixed(1)}%
          </span>
        )}
      </div>
      {isUpdated && (
        <div className="flex items-center gap-1 text-[10px] text-emerald-600">
          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
          Just updated
        </div>
      )}
    </div>
  );
};

export default useRealTimeDashboard;
